import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ModakBackdrop from "./ModakBackdrop";
import { useRoundGate } from "./useRoundGate";
import { useElapsedTimer } from "./useElapsedTimer";
import { submitModakRound } from "./modakApi";
import { RoundHeader } from "./RoundChrome";

/** Labels around the avatar — `pos` must match the chip `pos` (same as round5.php). */
const TARGETS = [
  { pos: 0, caption: "Big Head", top: 4, side: "right" },
  { pos: 1, caption: "Small eyes", top: 24, side: "right" },
  { pos: 2, caption: "Axe", top: 44, side: "right" },
  { pos: 3, caption: "Broken Trunk", top: 64, side: "right" },
  { pos: 4, caption: "Blessings", top: 84, side: "right" },
  { pos: 5, caption: "Big Ears", top: 4, side: "left" },
  { pos: 6, caption: "Curved Trunk", top: 24, side: "left" },
  { pos: 7, caption: "Modaka", top: 44, side: "left" },
  { pos: 8, caption: "Large Belly", top: 64, side: "left" },
  { pos: 9, caption: "Mouse", top: 84, side: "left" },
];

/** Word bank — shuffled order matches round5.php; correct drop when chip.pos === drop.pos. */
const CHIPS = [
  { pos: 2, text: "To cut ego and desires" },
  { pos: 3, text: "Retain the good and discard the bad" },
  { pos: 8, text: "Peacefully digest all good and bad in life" },
  { pos: 9, text: "Ride on desires but keep it under control" },
  { pos: 7, text: "Sweet rewards" },
  { pos: 5, text: "Importance of listening" },
  { pos: 1, text: "Focus and concentrate" },
  { pos: 0, text: "Think big, Intelligence" },
  { pos: 4, text: "Bless and protect us" },
  { pos: 6, text: "High efficiency and adaptability" },
];

const RULES = ["Drag and Drop the right words in the right boxes"];

/** Prefer visible drop zones (desktop vs mobile) via bounding-box hit test. */
function findDropPosAt(clientX, clientY) {
  const nodes = document.querySelectorAll("[data-drop-pos]");
  for (const el of nodes) {
    const rect = el.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) continue;
    if (
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom
    ) {
      return Number(el.dataset.dropPos);
    }
  }
  return null;
}

export default function Round5() {
  const navigate = useNavigate();
  const { loading, user, adminToken, timerKey, status } = useRoundGate(5);
  const time = useElapsedTimer(timerKey);

  const [score, setScore] = useState(0);
  const [placed, setPlaced] = useState({});
  const [draggingPos, setDraggingPos] = useState(null);
  const [dragXY, setDragXY] = useState({ x: 0, y: 0 });
  const [submitting, setSubmitting] = useState(false);

  const scoreRef = useRef(0);
  const placedRef = useRef({});
  const draggingPosRef = useRef(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const submittedRef = useRef(false);
  const basePointsRef = useRef(0);
  const chipOrder = useMemo(() => CHIPS, []);

  useEffect(() => {
    if (status) {
      scoreRef.current = status.points ?? 0;
      basePointsRef.current = scoreRef.current;
      setScore(scoreRef.current);
    }
  }, [status]);

  const finish = useCallback(
    async (finalPoints) => {
      if (submittedRef.current) return;
      submittedRef.current = true;
      setSubmitting(true);
      await submitModakRound(adminToken, {
        userId: user.userId,
        round: 5,
        points: finalPoints,
        time,
        answers: "",
        user,
      });
      navigate("/thankyou");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [adminToken, user, time, navigate]
  );

  const tryPlace = useCallback(
    (dragPos, dropPos) => {
      if (dragPos == null || dropPos == null) return;
      // Same rule as round5.php: only accept when drag pos === drop pos.
      if (Number(dragPos) !== Number(dropPos)) return;
      if (placedRef.current[dropPos]) return;

      const next = { ...placedRef.current, [dropPos]: true };
      placedRef.current = next;
      setPlaced(next);

      scoreRef.current += 1;
      setScore(scoreRef.current);

      if (Object.keys(next).length === 10) {
        finish(scoreRef.current);
      }
    },
    [finish]
  );

  useEffect(() => {
    const handleMove = (e) => {
      if (draggingPosRef.current == null) return;
      setDragXY({
        x: e.clientX - dragOffset.current.x,
        y: e.clientY - dragOffset.current.y,
      });
    };

    const handleUp = (e) => {
      const dragPos = draggingPosRef.current;
      if (dragPos == null) return;

      const dropPos = findDropPosAt(e.clientX, e.clientY);
      tryPlace(dragPos, dropPos);

      draggingPosRef.current = null;
      setDraggingPos(null);
    };

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleUp);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleUp);
    };
  }, [tryPlace]);

  if (loading) return null;

  const startDrag = (e, pos) => {
    if (placedRef.current[pos] || submitting) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const rect = e.currentTarget.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    setDragXY({ x: rect.left, y: rect.top });
    draggingPosRef.current = pos;
    setDraggingPos(pos);
  };

  const chipStyle = (pos) => {
    if (draggingPos !== pos) return undefined;
    return { left: dragXY.x, top: dragXY.y };
  };

  const renderChip = (chip, extraClass) => {
    const isPlaced = !!placed[chip.pos];
    const isDragging = draggingPos === chip.pos;
    return (
      <div
        key={chip.pos}
        className={`modak-r5-chip${isDragging ? " is-dragging" : ""}${isPlaced ? " is-placed" : ""} ${extraClass || ""}`}
        style={chipStyle(chip.pos)}
        onPointerDown={(e) => {
          if (isPlaced) return;
          startDrag(e, chip.pos);
        }}
      >
        {chip.text}
      </div>
    );
  };

  const bankChips = chipOrder.filter((c) => !placed[c.pos]);
  const allPlaced = Object.keys(placed).length === 10;

  const handleSkip = () => {
    if (submitting) return;
    finish(basePointsRef.current);
  };

  const dropSlot = (t) => (
    <div className="modak-r5-drop" data-drop-pos={t.pos}>
      {placed[t.pos] &&
        chipOrder.find((c) => c.pos === t.pos) &&
        renderChip(chipOrder.find((c) => c.pos === t.pos), "is-placed")}
    </div>
  );

  return (
    <ModakBackdrop>
      <div className="modak-round modak-r5-page">
        <RoundHeader time={time} score={score} rules={RULES} onSkip={handleSkip} />

        <div className="modak-round-heading-row">
          <span className="modak-round-label">Avatar</span>
          <div className="modak-round-instruction">Complete the avatar in this drag and drop game</div>
        </div>

        <div className="modak-r5-layout">
          <div className="modak-r5-image-wrap">
            <img src="/modak-assets/home/ganesha.png" alt="Ganesha outline" />
          </div>

          {TARGETS.map((t) => (
            <div
              key={t.pos}
              className={`modak-r5-label modak-r5-label--${t.side}`}
              style={{ top: `${t.top}%` }}
            >
              {t.side === "left" ? (
                <>
                  {dropSlot(t)}
                  <span className="modak-r5-caption">{t.caption}:</span>
                </>
              ) : (
                <>
                  <span className="modak-r5-caption">{t.caption}:</span>
                  {dropSlot(t)}
                </>
              )}
            </div>
          ))}
        </div>

        <div className="modak-r5-bank">{bankChips.map((c) => renderChip(c))}</div>

        {allPlaced && (
          <button
            type="button"
            className="modak-btn modak-submit-btn"
            disabled={submitting}
            onClick={() => finish(scoreRef.current)}
          >
            Submit
          </button>
        )}
      </div>
    </ModakBackdrop>
  );
}
